import { useCallback } from "react";
import type { CellKeyDownEvent } from "ag-grid-enterprise";

/**
 * Up and down arrows walk the rows and carry the selection with them, the way the logbook
 * grid does. Without it the arrows only move the focused cell and the detail stays behind.
 */
export const useArrowRowNavigation = <T>(onSelect: (row: T) => void) =>
	useCallback(
		(event: CellKeyDownEvent<T>) => {
			const { api, node, event: keyEvent } = event;
			if (!keyEvent) return;
			const key = (keyEvent as KeyboardEvent).key;
			if (key !== "ArrowUp" && key !== "ArrowDown") return;

			keyEvent.preventDefault();
			const currentIndex = node.rowIndex ?? -1;
			const nextIndex = key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1;
			const nextNode = api.getDisplayedRowAtIndex(nextIndex);
			if (!nextNode?.data) {
				// Past the first row the grid sends the focus to the header and the next ArrowDown
				// only brings it back: keep it on the row instead.
				if (event.column)
					setTimeout(() => api.setFocusedCell(currentIndex, event.column));
				return;
			}

			nextNode.setSelected(true);
			api.ensureIndexVisible(nextIndex);
			onSelect(nextNode.data);
		},
		[onSelect],
	);
